import { useMemo } from "react";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";
import { useListZones } from "@/api/orval/generated/zone";
import { useOrgDataStreams } from "../spec/use-org-data-streams";
import { type PickerStream, toPickerStream } from "./picker-stream";

export type CatalogStatus = "loading" | "error" | "ready";

export function usePickerCatalog({
    enabled,
    zoneId,
    plottedStreams,
    isPlottable,
}: {
    enabled: boolean;
    zoneId: string;
    plottedStreams: readonly ListDataStreams200Item[];
    isPlottable: (stream: ListDataStreams200Item) => boolean;
}): { plotted: PickerStream[]; catalog: PickerStream[]; status: CatalogStatus } {
    const org = useOrgDataStreams(enabled);
    const zones = useListZones({ query: { enabled } });

    const context = useMemo(() => {
        const names = new Map((zones.data ?? []).map((zone) => [zone._id, zone.name]));
        return { currentZoneId: zoneId, zoneName: (id: string) => names.get(id) };
    }, [zoneId, zones.data]);

    const plotted = useMemo(
        () => plottedStreams.map((item) => toPickerStream(item, context)),
        [plottedStreams, context],
    );
    const catalog = useMemo(
        () => (org.data ?? []).filter(isPlottable).map((item) => toPickerStream(item, context)),
        [org.data, isPlottable, context],
    );

    // Waits for zones too, or every row lands under "No zone" until they load.
    let status: CatalogStatus = "loading";
    if (org.data && zones.data) status = "ready";
    else if (org.isError || zones.isError) status = "error";

    return { plotted, catalog, status };
}
